<template>
  <BoxedFrame
    title="Offer"
    class="offer"
  >
    <section>
      <h3 class="title">
        {{ artistName }}
      </h3>
      <div class="divide-section">
        <div class="section grid-section">
          <div>
            <div class="row-label">
              Offer Date
            </div>
            <div>{{ formatDate(offerDate) }} - Today</div>
          </div>
          <div>
            <div class="row-label">
              Offer Expiration Date
            </div>
            <div>
              {{ formatDate(offerExpirationDate) }}
            </div>
          </div>
          <div>
            <div class="row-label">
              Venue
            </div>
            <div>
              {{ venueName }}
            </div>
          </div>
          <div>
            <div class="row-label">
              Venue Address
            </div>
            <div>
              {{ venueAddress }}
            </div>
          </div>
          <div>
            <div class="row-label">
              Artist Performance Dates
            </div>
            <Form
              :form-id="offerFormId"
              :initial-values="offerSheet || {}"
              :class="{ dirty }"
            >
              <span>
                <VPInput
                  :disabled="disabled"
                  input-id="performanceDatesDescription"
                  placeholder="Add performance dates"
                  class="overflow-x-auto"
                  @blur="performanceDatesSave"
                />
              </span>
            </Form>
          </div>
          <div class="row-label">
            <VPButton
              label="Autofill with performance dates"
              theme="primary"
              size="xs"
              class="min-w-fit p-1"
              @click.prevent="generatePerformanceDates"
            />
          </div>
        </div>
        <div>
          <AgentSection
            :form-id="agentFormId"
            :initial-values="agent || {}"
            :disabled="agentLoading"
            class="section grid-section"
            @blur="onAgentRowBlur"
            @save="onAgentRowSave"
          />
        </div>
        <div>
          <DealSection />
        </div>
      </div>
    </section>
  </BoxedFrame>
</template>
<script setup lang="ts">
import BoxedFrame from '@/components/BoxedFrame.vue';
import AgentSection from '@/components/offer-sheet/offer/AgentSection.vue';
import DealSection from '@/components/offer-sheet/offer/DealSection.vue';
import { computed } from 'vue';
import { type SaveAgentParams, useOfferSheetStore } from '@/stores/offer-sheet';
import { Form, useFormsStore } from 'vuniform';
import { formatDate } from '@/helpers/dateTime';
import { take } from '@/helpers/takePayload';
import VPButton from '@/components/form/VPButton.vue';
import VPInput from '@/components/form/VPInput.vue';

const offerSheetStore = useOfferSheetStore();
const formsStore = useFormsStore();

const agentFormId = 'offer-sheets-agent';
const offerFormId = 'OFFER_FORM_ID';

const offerSheet = computed(() => offerSheetStore.offerSheet);
const agentLoading = computed(() => offerSheetStore.agentLoading);
const offerDate = computed(() => offerSheet.value.offerDate);
const offerExpirationDate = computed(() => offerSheet.value.offerExpirationDate);
const venueName = computed(() => offerSheet.value.venueName);
const venueAddress = computed(() => offerSheet.value.venueAddress);
const agent = computed(() => offerSheetStore.offerSheet.agent);
const artistName = computed(() => offerSheetStore.offerSheet.artistName);
const loading = computed(() => offerSheetStore.offerSheetLoading);

const onAgentRowBlur = () => {
  onAgentRowSave();
};

const onAgentRowSave = async () => {
  if (agentLoading.value || !formsStore.isFormDirty(agentFormId)) {
    return;
  }
  const agentParams = formsStore.formGetValues(agentFormId);
  const payload: SaveAgentParams = Object.assign({}, offerSheet.value.agent, agentParams);
  payload.offerSheetId = offerSheet.value.id;
  offerSheetStore.upsertAgent(take(payload, 'id', 'name', 'email', 'agencyName', 'offerSheetId'));

  formsStore.SET_INITIAL_VALUES({
    values: { ...agent.value },
    formId: agentFormId,
    clearDirty: true,
  });
};

const generatePerformanceDates = async () => {
  await offerSheetStore.generatePerformanceDates({
    offerSheetId: offerSheet.value.id,
  });

  formsStore.SET_INITIAL_VALUES({
    values: { ...offerSheet.value },
    formId: offerFormId,
    clearDirty: true,
  });

  formsStore.RESET_FIELD_VALUES(offerFormId);
};

const performanceDatesSave = () => {
  if (loading.value || !formsStore.isFieldDirty(offerFormId, 'performanceDatesDescription')) {
    return;
  }

  offerSheetStore.updateOfferSheet({
    id: offerSheet.value.id,
    performanceDatesDescription: formsStore.fieldGetValue(offerFormId, 'performanceDatesDescription'),
  });

  formsStore.SET_INITIAL_VALUES({
    values: { ...offerSheet.value },
    formId: offerFormId,
    clearDirty: true,
  });
};
</script>
